/**
 * <s-header-fx>
 * Interactive header, mode read from the owning <header data-header-fx> (theme setting „Header-Effekt“).
 * All modes: the header sticks to the top, hides while the shopper scrolls down and slides back,
 * slimmer (data-compact), as soon as they scroll up. It never hides while the mobile menu is open,
 * the pointer is over the header (mega menu) or focus is inside it.
 * emboss / combo: the logo tilts toward the pointer, a brass sheen follows it, one sweep on page load.
 * smart: the logo leans magnetically toward the pointer, a brass line glides under the hovered menu link.
 * summit: --hfx-p (0–1) drives the brass scroll-progress line, the logo flickers on hover.
 * Pointer effects only run for fine pointers without reduced motion.
 */
const HIDE_AFTER = 160;
const COMPACT_AT = 120;
const SCROLL_DELTA = 6;
const MAX_TILT = 9;
const MAGNET_RADIUS = 180;
const MAGNET_MAX = 8;

class SHeaderFx extends HTMLElement {
  #header = null;
  #logo = null;
  #mode = 'none';
  #ac = null;
  #frame = 0;
  #lastY = 0;
  #timers = [];

  connectedCallback() {
    this.#header = this.closest('header');
    if (!this.#header) return;
    this.#mode = this.#header.dataset.headerFx || 'none';
    if (this.#mode === 'none') return;

    this.#logo = this.#header.querySelector('[data-hfx-logo]');
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    // Glass header: publish the real header height as --ma-header-h, so the hero/banner pulled up under it
    // (.ma-merge-top) lines up to the pixel. Measured only in the full-size state, not while compact.
    const root = document.documentElement;
    const measure = () => {
      if (this.#header.hasAttribute('data-compact')) return;
      const h = this.#header.offsetHeight;
      if (h > 0) root.style.setProperty('--ma-header-h', `${h}px`);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(this.#header);
    signal.addEventListener('abort', () => ro.disconnect());

    // Mega menu: publish the panel height as --ma-mega-h, so the header glass can grow down over the open panel
    // (one continuous glass sheet, no seam at the header edge). The panel keeps its layout while hidden.
    const mega = this.#header.querySelector('.ma-mega-glass');
    if (mega) {
      const measureMega = () => {
        const mh = mega.offsetHeight;
        if (mh > 0) this.#header.style.setProperty('--ma-mega-h', `${mh}px`);
      };
      measureMega();
      const megaRo = new ResizeObserver(measureMega);
      megaRo.observe(mega);
      signal.addEventListener('abort', () => megaRo.disconnect());
    }

    this.#lastY = window.scrollY;
    window.addEventListener('scroll', () => this.#queue(), { passive: true, signal });
    window.addEventListener('resize', () => this.#queue(), { passive: true, signal });
    this.#update();

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !this.#logo) return;

    const emboss = this.#mode === 'emboss' || this.#mode === 'combo';
    if (emboss) this.#sweep();

    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!fine) return;
    if (emboss) this.#listenTilt(signal);
    if (this.#mode === 'smart') {
      this.#listenMagnet(signal);
      this.#listenUnderline(signal);
    }
    if (this.#mode === 'summit') this.#listenFlicker(signal);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
    this.#timers.forEach((timer) => clearTimeout(timer));
  }

  #queue() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  }

  #locked() {
    const nav = this.#header.querySelector('s-header-nav');
    return Boolean(nav?.hasAttribute('open')) || this.#header.matches(':hover') || this.#header.matches(':focus-within');
  }

  #update() {
    const y = Math.max(0, window.scrollY);
    const header = this.#header;

    header.toggleAttribute('data-compact', y > COMPACT_AT);
    // Glass header (29 Oct 2026): fully transparent at the very top, the frosted background fades in once the page moves.
    header.toggleAttribute('data-scrolled', y > 8);

    const delta = y - this.#lastY;
    // Immersive product page on phones: header stays out of view over the first image and slides in
    // once the shopper has scrolled about a third of the screen; from then on it stays (no hide on scroll down).
    const immersive = document.body.hasAttribute('data-immersive-pdp');
    header.toggleAttribute('data-immersive-show', immersive && y > window.innerHeight * 0.35);
    if (immersive) {
      header.removeAttribute('data-hidden');
      this.#lastY = y;
    } else if (y < HIDE_AFTER) {
      header.removeAttribute('data-hidden');
      this.#lastY = y;
    } else if (Math.abs(delta) > SCROLL_DELTA) {
      if (delta > 0 && !this.#locked()) header.setAttribute('data-hidden', '');
      if (delta < 0) header.removeAttribute('data-hidden');
      this.#lastY = y;
    }

    if (this.#mode === 'summit') {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(1, y / max) : 0;
      header.style.setProperty('--hfx-p', progress.toFixed(4));
    }
  }

  #sweep() {
    this.#logo.setAttribute('data-sweep', '');
    this.#timers.push(setTimeout(() => this.#logo?.removeAttribute('data-sweep'), 2400));
  }

  #listenTilt(signal) {
    const logo = this.#logo;
    logo.addEventListener(
      'pointermove',
      (event) => {
        const rect = logo.getBoundingClientRect();
        const fx = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        const fy = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
        logo.setAttribute('data-hover', '');
        logo.style.setProperty('--hfx-rx', `${((fx - 0.5) * 2 * MAX_TILT).toFixed(2)}deg`);
        logo.style.setProperty('--hfx-ry', `${(-(fy - 0.5) * 2 * MAX_TILT).toFixed(2)}deg`);
        logo.style.setProperty('--hfx-sx', `${(((1.5 - fx) / 2) * 100).toFixed(1)}%`);
      },
      { signal }
    );
    logo.addEventListener(
      'pointerleave',
      () => {
        logo.removeAttribute('data-hover');
        logo.style.removeProperty('--hfx-rx');
        logo.style.removeProperty('--hfx-ry');
        logo.style.removeProperty('--hfx-sx');
      },
      { signal }
    );
  }

  #listenMagnet(signal) {
    const logo = this.#logo;
    const header = this.#header;
    header.addEventListener(
      'pointermove',
      (event) => {
        const rect = logo.getBoundingClientRect();
        const dx = event.clientX - (rect.left + rect.width / 2);
        const dy = event.clientY - (rect.top + rect.height / 2);
        const distance = Math.hypot(dx, dy);
        const reach = MAGNET_RADIUS + rect.width / 2;
        if (distance > reach) {
          logo.style.removeProperty('--hfx-mx');
          logo.style.removeProperty('--hfx-my');
          return;
        }
        const pull = 1 - distance / reach;
        const clamp = (value) => Math.max(-MAGNET_MAX, Math.min(MAGNET_MAX, value));
        logo.style.setProperty('--hfx-mx', `${clamp(dx * 0.12 * pull).toFixed(2)}px`);
        logo.style.setProperty('--hfx-my', `${clamp(dy * 0.18 * pull).toFixed(2)}px`);
      },
      { signal }
    );
    header.addEventListener(
      'pointerleave',
      () => {
        logo.style.removeProperty('--hfx-mx');
        logo.style.removeProperty('--hfx-my');
      },
      { signal }
    );
  }

  #listenUnderline(signal) {
    const line = this.#header.querySelector('[data-hfx-underline]');
    const menu = this.#header.querySelector('.header-menu');
    if (!line || !menu) return;

    const moveTo = (link) => {
      const headerRect = this.#header.getBoundingClientRect();
      const rect = link.getBoundingClientRect();
      // Coming from hidden: jump into place and only fade in, glide between links afterwards.
      if (line.style.opacity !== '1') {
        line.style.transition = 'opacity 0.25s ease';
        requestAnimationFrame(() => requestAnimationFrame(() => line.style.removeProperty('transition')));
      }
      line.style.width = `${rect.width}px`;
      line.style.transform = `translate(${rect.left - headerRect.left}px, ${rect.bottom - headerRect.top + 6}px)`;
      line.style.opacity = '1';
    };

    menu.querySelectorAll(':scope > li').forEach((item) => {
      const link = item.querySelector('a');
      if (!link) return;
      item.addEventListener('pointerenter', () => moveTo(link), { signal });
      link.addEventListener('focus', () => moveTo(link), { signal });
    });
    menu.addEventListener('pointerleave', () => (line.style.opacity = '0'), { signal });
    menu.addEventListener('focusout', () => (line.style.opacity = '0'), { signal });
  }

  #listenFlicker(signal) {
    const logo = this.#logo;
    logo.addEventListener(
      'pointerenter',
      () => {
        if (logo.hasAttribute('data-flicker')) return;
        logo.setAttribute('data-flicker', '');
        this.#timers.push(setTimeout(() => logo.removeAttribute('data-flicker'), 900));
      },
      { signal }
    );
  }
}

if (!customElements.get('s-header-fx')) customElements.define('s-header-fx', SHeaderFx);
